import { apiClient } from '@/lib/apiClient';
import { Business, ActivityForHomepage } from '@/types/homepage';
import { AxiosError } from 'axios';

/**
 * Fetches a public business profile by ID
 * @param businessId - The ID of the business to fetch
 * @returns Promise<Business>
 */
export const fetchPublicBusinessProfile = async (businessId: string): Promise<Business> => {
  if (!businessId) {
    console.error('fetchPublicBusinessProfile called without businessId');
    throw new Error('Business ID is required');
  }

  try {
    const response = await apiClient.get(`/business/public/${businessId}`);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Invalid response format from server');
    }

    const businessData = response.data.data;

    // Transform to match the Business type
    const business: Business = {
      _id: businessData._id,
      name: businessData.name,
      emoji: businessData.emoji || '🏢',
      description: businessData.description || '',
      type: businessData.type || 'tour_operator',
      status: businessData.status || 'active',
      slug: businessData.slug || '',
      illustrativeImages: businessData.illustrativeImages,
      location: businessData.location || {
        address: { city: '', country: '' },
        coordinates: { coordinates: [0, 0] }
      },
      contact: businessData.contact || {
        email: '',
        bookingMode: 'platform'
      },
      images: businessData.images || {
        gallery: []
      },
      hasAnimalActivities: businessData.hasAnimalActivities,
      hasAdventureActivities: businessData.hasAdventureActivities,
      statistics: businessData.statistics || {
        totalBookings: 0,
        averageRating: 0,
        completionRate: 0
      }
    };

    return business;
  } catch (error) {
    console.error('Error fetching public business profile:', error);
    throw error;
  }
};

/**
 * Fetches public business activities by ID
 * @param businessId - The ID of the business to fetch activities for
 * @returns Promise<ActivityForHomepage[]>
 */
export const fetchPublicBusinessActivities = async (businessId: string): Promise<ActivityForHomepage[]> => {
  if (!businessId) {
    throw new Error('Business ID is required');
  }

  try {
    const response = await apiClient.get(`/animal-activities/business/${businessId}`);
    
    if (!response.data || !Array.isArray(response.data)) {
      return [];
    }

    // Transform activities to match ActivityForHomepage type
    const activities: ActivityForHomepage[] = response.data.map(activity => ({
      _id: activity._id,
      name: activity.name,
      description: activity.description,
      type: activity.type || 'business',
      duration: activity.duration || 0,
      maxParticipants: activity.maxParticipants || 10,
      minParticipants: activity.minParticipants || 1,
      activityType: activity.activityType as 'animal' | 'adventure',
      emoji: activity.emoji || '🌟',
      images: activity.images || [], // Include activity images
      seasons: activity.seasons || [],
      bookingUrl: activity.bookingUrl,
      price: activity.price ? {
        amount: activity.price.amount || 0,
        currency: activity.price.currency || 'USD',
        isFree: activity.price.isFree || false
      } : undefined,
      location: activity.location ? {
        city: activity.location.city,
        country: activity.location.country,
        coordinates: activity.location.coordinates ? {
          lat: activity.location.coordinates[1],
          lng: activity.location.coordinates[0]
        } : undefined
      } : undefined,
      status: activity.status || 'active',
      businessId: activity.businessId || '',
      createdAt: activity.createdAt || new Date().toISOString(),
      updatedAt: activity.updatedAt || new Date().toISOString(),
      isFree: activity.price?.isFree || false
    }));

    return activities;
  } catch (error) {
    console.error('Error fetching public business activities:', error);
    // A business with zero activities yet (e.g. newly onboarded) isn't a page-level
    // failure — degrade like the sibling custom-adventures fetchers instead of
    // sinking the whole Promise.all in BusinessProfilePage.
    return [];
  }
};

/**
 * Fetches public custom adventures by business ID
 * @param businessId - The ID of the business to fetch custom adventures for
 * @returns Promise<CustomAdventure[]>
 */
export const fetchPublicCustomAdventures = async (businessId: string): Promise<any[]> => {
  if (!businessId) {
    throw new Error('Business ID is required');
  }

  try {
    const response = await apiClient.get(`/custom-adventures`, {
      params: { businessId }
    });

    if (!response.data || !response.data.success || !Array.isArray(response.data.data)) {
      return [];
    }

    // Transform and return custom adventures data
    return response.data.data.map((adventure: any) => ({
      _id: adventure._id || adventure.id,
      id: adventure._id || adventure.id,
      name: adventure.name,
      description: adventure.description,
      basePrice: adventure.basePrice,
      currency: adventure.currency,
      images: adventure.images || [],
      options: adventure.options || [],
      isActive: adventure.isActive,
      published: adventure.published,
      createdAt: adventure.createdAt,
      updatedAt: adventure.updatedAt
    }));
  } catch (error) {
    console.error('Error fetching public custom adventures:', error);
    return [];
  }
};

/**
 * Checks if a business offers custom adventures
 * @param businessId - The ID of the business to check
 * @returns Promise<{ hasCustomAdventures: boolean }>
 */
export const checkPublicCustomAdventuresAvailability = async (businessId: string): Promise<{ hasCustomAdventures: boolean }> => {
  if (!businessId) {
    throw new Error('Business ID is required');
  }

  try {
    // Get published custom adventures for the business
    const response = await apiClient.get(`/custom-adventures`, {
      params: { businessId }
    });

    // The backend returns { success: boolean, data: CustomAdventure[], message?: string }
    // If success is true and data array has items, the business has custom adventures
    return { 
      hasCustomAdventures: response.data?.success === true && Array.isArray(response.data?.data) && response.data.data.length > 0 
    };
  } catch (error) {
    console.error('Error checking custom adventures availability:', error);
    // If endpoint returns 404 or any other error, assume no custom adventures
    return { hasCustomAdventures: false };
  }
}; 